/**
 * SwapClientDialog - Reusable dialog for switching to a different client
 * Can be used anywhere client switching is needed (OTT Manager, etc.)
 */
import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/client/components/ui/dialog';
import { callCrudApi } from '@/client/utils/crud';
import type { UserMetadata } from '@shared/types';
import type { SwapUserClientPayload } from '@shared/types/crud';

const USER_METADATA_KEY = 'http://invintus_user_metadata';

interface SwapClientDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** The client ID to swap to */
  targetClientID: string;
  /** The client name to swap to (for display) */
  targetClientName: string;
  /** Whether there are unsaved changes that would be lost */
  hasUnsavedChanges?: boolean;
  /** Optional callback after successful swap (before page refresh) */
  onSwapSuccess?: () => void;
  /** Optional URL to open after swap completes */
  openUrlAfterSwap?: string;
}

export function SwapClientDialog({
  open,
  onOpenChange,
  targetClientID,
  targetClientName,
  hasUnsavedChanges = false,
  onSwapSuccess,
  openUrlAfterSwap,
}: SwapClientDialogProps) {
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const [swapping, setSwapping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current user's metadata
  const userMetadata = user?.[USER_METADATA_KEY] as UserMetadata | undefined;
  const currentUserLoginID = userMetadata?.loginID ? String(userMetadata.loginID) : '';
  const currentClientName = userMetadata?.clientID ? `Client ${userMetadata.clientID}` : 'current client';

  const handleSwap = async () => {
    if (!currentUserLoginID) {
      setError('Unable to determine your user ID. Please try refreshing the page.');
      return;
    }

    setSwapping(true);
    setError(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const payload: SwapUserClientPayload = {
        userID: currentUserLoginID,
        clientID: targetClientID,
      };

      const response = await callCrudApi('/Users/swapUserAcc', payload, accessToken);

      if (response.success) {
        onSwapSuccess?.();

        // If we need to open a URL after swap, store it for after refresh
        if (openUrlAfterSwap) {
          sessionStorage.setItem('openAfterSwap', openUrlAfterSwap);
        }

        // Silent re-auth to refresh user metadata from Auth0
        setTimeout(() => {
          loginWithRedirect({
            authorizationParams: { prompt: 'none' },
            appState: { returnTo: window.location.pathname },
          });
        }, 1500);
      } else {
        setError('Swap failed. Please try again.');
        setSwapping(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setSwapping(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={swapping ? undefined : onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Switch Client</DialogTitle>
          <DialogDescription>
            Switch your session to <strong>{targetClientName}</strong> to access this app's editor.
          </DialogDescription>
        </DialogHeader>

        {hasUnsavedChanges && (
          <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Warning:</strong> You have unsaved changes that will be lost when the page refreshes.
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950 rounded-md">
            {error}
          </div>
        )}

        {swapping && (
          <div className="flex items-center justify-center gap-3 py-4">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Switching client and refreshing...</span>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={swapping}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSwap}
            disabled={swapping}
          >
            {swapping ? 'Switching...' : 'Switch Client'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hook to get current user's client info and check if swap is needed
 */
export function useClientSwapInfo(targetClientID: string) {
  const { user } = useAuth0();

  const userMetadata = user?.[USER_METADATA_KEY] as UserMetadata | undefined;
  const currentUserClientID = userMetadata?.clientID ? String(userMetadata.clientID) : '';
  const needsSwap = Boolean(targetClientID && currentUserClientID && targetClientID !== currentUserClientID);

  return {
    currentUserClientID,
    needsSwap,
  };
}
